import { useEffect, useState, useCallback, useRef } from 'react'
import { lumi } from '@/lib/lumi'
import {Plus, Trash2, Loader2, X, ToggleLeft, ToggleRight, Link as LinkIcon, Upload} from 'lucide-react'
import toast from 'react-hot-toast'
import type { Banner } from '@/types'
import { useAuth } from '@/hooks/useAuth'

const emptyForm = {
  title: '',
  mediaUrl: '',
  mediaType: 'image' as Banner['mediaType'],
  linkUrl: '',
  position: 'header' as Banner['position'],
  isActive: true,
  order: 0,
}

/* ── Upload de banner (630×90) ── */
function BannerUploader({
  value,
  mediaType,
  onChange,
  onTypeChange,
}: {
  value: string
  mediaType: Banner['mediaType']
  onChange: (url: string) => void
  onTypeChange: (type: Banner['mediaType']) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const detectType = (file: File): Banner['mediaType'] => {
    if (file.type === 'image/gif') return 'gif'
    if (file.type.startsWith('video/')) return 'video'
    return 'image'
  }

  const handleFile = async (file: File) => {
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm']
    if (!allowed.includes(file.type)) {
      toast.error('Formato não suportado. Use JPG, PNG, GIF, WebP, MP4 ou WebM.')
      return
    }
    setUploading(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const result = results[0]
      if (result.fileUrl) {
        onChange(result.fileUrl)
        onTypeChange(detectType(file))
        toast.success('Ficheiro do banner carregado!')
      } else {
        toast.error(result.uploadError || 'Erro ao carregar ficheiro.')
      }
    } catch {
      toast.error('Erro ao carregar ficheiro.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
        Ficheiro do Banner * <span className="text-gray-400 font-normal text-xs">(630×90 px)</span>
      </label>

      {value ? (
        <div className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 group" style={{ height: '90px' }}>
          {mediaType === 'video' ? (
            <video src={value} className="w-full h-full object-cover" muted loop autoPlay playsInline />
          ) : (
            <img src={value} alt="Prévia do banner" className="w-full h-full object-contain" />
          )}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-800 text-xs font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              Substituir
            </button>
            <button
              type="button"
              onClick={() => onChange('')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-colors"
            >
              <X size={12} /> Remover
            </button>
          </div>
          <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
            630×90
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-center gap-2 hover:border-red-400 dark:hover:border-red-500 transition-colors group"
          style={{ height: '90px' }}
        >
          {uploading ? (
            <Loader2 size={20} className="animate-spin text-red-500" />
          ) : (
            <Upload size={20} className="text-gray-300 dark:text-gray-600 group-hover:text-red-400 transition-colors" />
          )}
          <div className="text-left">
            <p className="text-sm text-gray-400 dark:text-gray-500 group-hover:text-red-400 transition-colors">
              {uploading ? 'A carregar...' : 'Clique para fazer upload do banner'}
            </p>
            <p className="text-xs text-gray-300 dark:text-gray-600">JPG, PNG, GIF, WebP, MP4, WebM — 630×90 px</p>
          </div>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm"
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0]
          if (file) handleFile(file)
          e.target.value = ''
        }}
      />
    </div>
  )
}

export default function ManageBanners() {
  const { user } = useAuth()
  const [banners, setBanners] = useState<Banner[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [confirmDelete, setConfirmDelete] = useState<Banner | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  const fetchBanners = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.banners.list({ sort: { position: 1, order: 1 }, limit: 100 })
      setBanners(list as Banner[])
    } catch (e) {
      console.error('Erro ao carregar banners:', e)
      toast.error('Erro ao carregar banners.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchBanners() }, [fetchBanners])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim()) {
      toast.error('O título é obrigatório.')
      return
    }
    if (!form.mediaUrl.trim()) {
      toast.error('É necessário fazer upload do ficheiro do banner.')
      return
    }
    setSaving(true)
    try {
      const now = new Date().toISOString()
      await lumi.entities.banners.create({
        ...form,
        order: Number(form.order) || 0,
        creator: user?.userId || '',
        createdAt: now,
        updatedAt: now,
      })
      toast.success('Banner adicionado com sucesso!')
      setForm(emptyForm)
      setShowForm(false)
      fetchBanners()
    } catch (e) {
      console.error('Erro ao salvar banner:', e)
      toast.error('Erro ao salvar banner.')
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (banner: Banner) => {
    try {
      await lumi.entities.banners.update(banner._id, { isActive: !banner.isActive, updatedAt: new Date().toISOString() })
      setBanners(prev => prev.map(b => b._id === banner._id ? { ...b, isActive: !b.isActive } : b))
      toast.success(banner.isActive ? 'Banner desativado.' : 'Banner ativado.')
    } catch {
      toast.error('Erro ao atualizar banner.')
    }
  }

  const handleDelete = async (banner: Banner) => {
    setDeleting(banner._id)
    try {
      await lumi.entities.banners.delete(banner._id)
      toast.success('Banner excluído com sucesso.')
      setBanners(prev => prev.filter(b => b._id !== banner._id))
    } catch {
      toast.error('Erro ao excluir banner.')
    } finally {
      setDeleting(null)
      setConfirmDelete(null)
    }
  }

  const headerBanners = banners.filter(b => b.position === 'header')
  const interBanners = banners.filter(b => b.position === 'between_articles')

  const BannerSection = ({ title, items }: { title: string; items: Banner[] }) => (
    <div className="mb-8">
      <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">{title}</h2>
      {items.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 py-8 text-center text-sm text-gray-400">
          Nenhum banner nesta posição.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(banner => (
            <div key={banner._id} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4 flex items-center gap-4">
              {/* Preview 630×90 em miniatura */}
              <div className="w-24 h-[36px] rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0 border border-gray-200 dark:border-gray-700">
                {banner.mediaType === 'video' ? (
                  <video src={banner.mediaUrl} className="w-full h-full object-cover" muted />
                ) : (
                  <img src={banner.mediaUrl} alt={banner.title} className="w-full h-full object-contain" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{banner.title}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-xs text-gray-400 capitalize">{banner.mediaType}</span>
                  <span className="text-xs text-gray-300">•</span>
                  <span className="text-xs text-gray-400">630×90 px</span>
                  {banner.linkUrl && (
                    <span className="flex items-center gap-0.5 text-xs text-blue-500">
                      <LinkIcon size={11} /> Link
                    </span>
                  )}
                  <span className="text-xs text-gray-400">Ordem: {banner.order ?? 0}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => handleToggle(banner)}
                  className={`transition-colors ${banner.isActive ? 'text-green-500 hover:text-green-600' : 'text-gray-300 dark:text-gray-600 hover:text-gray-500'}`}
                  aria-label={banner.isActive ? 'Desativar' : 'Ativar'}
                >
                  {banner.isActive ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                </button>
                <button
                  onClick={() => setConfirmDelete(banner)}
                  disabled={deleting === banner._id}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 transition-all"
                  aria-label="Excluir"
                >
                  {deleting === banner._id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gerenciar Banners</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Todos os banners são exibidos no tamanho 630×90 px</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <Plus size={16} />
          Novo Banner
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-red-600" /></div>
      ) : (
        <>
          <BannerSection title="Cabeçalho (Patrocinador)" items={headerBanners} />
          <BannerSection title="Entre Matérias (Rotativos)" items={interBanners} />
        </>
      )}

      {/* Modal Novo Banner */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Novo Banner</h3>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">

              {/* Upload */}
              <BannerUploader
                value={form.mediaUrl}
                mediaType={form.mediaType}
                onChange={url => setForm(f => ({ ...f, mediaUrl: url }))}
                onTypeChange={type => setForm(f => ({ ...f, mediaType: type }))}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Título / Descrição *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Ex: Banner Patrocinador Principal"
                  required
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Tipo de Mídia</label>
                  <select
                    value={form.mediaType}
                    onChange={e => setForm(f => ({ ...f, mediaType: e.target.value as Banner['mediaType'] }))}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                  >
                    <option value="image">Imagem (JPG/PNG/WebP)</option>
                    <option value="gif">GIF</option>
                    <option value="video">Vídeo (MP4/WebM)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Posição</label>
                  <select
                    value={form.position}
                    onChange={e => setForm(f => ({ ...f, position: e.target.value as Banner['position'] }))}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                  >
                    <option value="header">Cabeçalho (Patrocinador)</option>
                    <option value="between_articles">Entre Matérias</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Link de Destino (opcional)</label>
                <input
                  type="url"
                  value={form.linkUrl}
                  onChange={e => setForm(f => ({ ...f, linkUrl: e.target.value }))}
                  placeholder="https://anunciante.com"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Ordem de Exibição</label>
                  <input
                    type="number"
                    value={form.order}
                    onChange={e => setForm(f => ({ ...f, order: Number(e.target.value) }))}
                    min={0}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                  />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                      className="w-4 h-4 accent-red-600"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Ativo</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 size={15} className="animate-spin" /> : null}
                  Salvar Banner
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmar exclusão */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Excluir Banner</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              Tem certeza que deseja excluir <strong className="text-gray-700 dark:text-gray-300">"{confirmDelete.title}"</strong>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                disabled={!!deleting}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? <Loader2 size={15} className="animate-spin" /> : null}
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
